<template>
    <div
        ref="sideNav"
        :class="{ 'is-blurred': isBlurred, 'side-nav--new-ui': isNewUi }"
        class="side-nav"
        data-cy="side-nav">
        <nav
            :aria-label="$t('sideNav.Main')"
            class="side-nav__content"
            :class="{ expanded, 'sales-trial-expired': isOnTrialExpiredRoute }">
            <div
                class="side-nav__header"
                :class="{ expanded }"
                :style="headerMarginStyle">
                <expand-collapse-nav-button />
                <logo
                    v-if="expanded && !hasPurchasedAdoptEndUser"
                    class="side-nav__logo"
                    :use-dark-logo="isPendoDarkMode" />
            </div>
            <logo-oem
                v-if="expanded && hasPurchasedAdoptEndUser"
                class="side-nav__logo" />
            <global-create
                :expanded="expanded"
                :open-popover="openPopover" />

            <ul class="nav-list">
                <li
                    v-if="shouldShowHomeNavItem"
                    class="nav-item home-link">
                    <home-nav-item
                        :expanded="expanded"
                        :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.HOME)"
                        :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.HOME) && 'page'" />
                </li>
                <li class="nav-item dashboards-link">
                    <dashboards-nav-item
                        :expanded="expanded"
                        :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.DASHBOARDS)"
                        :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.DASHBOARDS) && 'page'" />
                </li>
                <li
                    v-if="shouldShowSpacesNavItem"
                    class="nav-item spaces-link">
                    <spaces-nav-item
                        :expanded="expanded"
                        :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.SPACES)"
                        :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.SPACES) && 'page'" />
                </li>
                <li class="nav-item jump-to-menu">
                    <jump-to
                        :expanded="expanded"
                        :open-popover="openPopover"
                        @handle-popover="handlePopover"
                        @mouseenter="handleMouseenter"
                        @mouseleave="handleMouseleave" />
                </li>
                <li class="nav-item recents-link">
                    <quick-access
                        :expanded="expanded"
                        :open-popover="openPopover"
                        @handle-popover="handlePopover"
                        @mouseenter="handleMouseenter"
                        @mouseleave="handleMouseleave" />
                </li>
            </ul>
            <div class="nav-section">
                <h2
                    v-if="expanded"
                    class="nav-title">
                    {{ $t('sideNav.Learn') }}
                </h2>
                <pendo-collapse
                    v-model="expandedNavOption"
                    accordion
                    class="nav-section__collapse">
                    <ul class="nav-list">
                        <li
                            v-for="option in filterNavOptionsByPermissions(learnNavOptions)"
                            :key="option.name">
                            <nav-section-collapsible-item
                                v-if="hasChildren(option) && expanded"
                                :option="option"
                                :active-option="activeNavOption" />
                            <nav-section-popover
                                v-else
                                :option="option"
                                :active-option="activeNavOption"
                                :expanded="expanded"
                                :open-popover="openPopover"
                                @handle-popover="handlePopover"
                                @mouseenter="handleMouseenter"
                                @mouseleave="handleMouseleave" />
                        </li>
                    </ul>
                </pendo-collapse>
            </div>

            <div
                v-if="filterNavOptionsByPermissions(actNavOptions).length"
                class="nav-section">
                <h2
                    v-if="expanded"
                    class="nav-title">
                    {{ $t('sideNav.Act') }}
                </h2>
                <pendo-collapse
                    v-model="expandedNavOption"
                    accordion
                    class="nav-section__collapse">
                    <ul class="nav-list">
                        <li
                            v-for="option in filterNavOptionsByPermissions(actNavOptions)"
                            :key="option.name">
                            <nav-section-collapsible-item
                                v-if="hasChildren(option) && expanded"
                                :option="option"
                                :active-option="activeNavOption" />
                            <nav-section-popover
                                v-else
                                :option="option"
                                :active-option="activeNavOption"
                                :expanded="expanded"
                                :open-popover="openPopover"
                                @handle-popover="handlePopover"
                                @mouseenter="handleMouseenter"
                                @mouseleave="handleMouseleave" />
                        </li>
                    </ul>
                </pendo-collapse>
            </div>

            <div
                v-if="showAvailableUpgradeModules"
                class="available-upgrade-items"
                :class="{ expanded }"
                data-cy="available-upgrade-nav-items">
                <hr class="available-upgrade-items__separator">
                <h2
                    v-if="expanded"
                    class="nav-title">
                    {{ $t('sideNav.Available upgrades') }}
                </h2>
                <ul>
                    <li
                        v-if="showOrchestrateSplashPage"
                        class="nav-item orchestrate-link">
                        <orchestrate-nav-item
                            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE_UPSELL)"
                            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE_UPSELL) && 'page'"
                            :expanded="expanded" />
                    </li>
                    <li
                        v-if="!hasPurchasedAnalytics"
                        class="nav-item analytics-link">
                        <analytics-nav-item
                            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.ANALYTICS_UPSELL)"
                            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.ANALYTICS_UPSELL) && 'page'"
                            :expanded="expanded" />
                    </li>
                    <li
                        v-if="!hasPurchasedSurveys && !hasPurchasedNPS && !hasPurchasedAdopt && !hasPurchasedAdoptReseller"
                        class="nav-item surveys-link">
                        <surveys-nav-item
                            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.SURVEYS_UPSELL)"
                            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.SURVEYS_UPSELL) && 'page'"
                            :expanded="expanded" />
                    </li>
                    <li
                        v-if="!hasPurchasedGuides"
                        class="nav-item guides-link">
                        <guides-nav-item
                            is-basic-upgrade
                            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.GUIDES_UPSELL)"
                            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.GUIDES_UPSELL) && 'page'"
                            :expanded="expanded" />
                    </li>
                    <li
                        v-if="!hasPurchasedGuidesPro"
                        class="nav-item guides-link">
                        <guides-nav-item
                            is-pro-upgrade
                            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.GUIDES_PRO_UPSELL)"
                            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.GUIDES_PRO_UPSELL) && 'page'"
                            :expanded="expanded" />
                    </li>
                    <li
                        v-if="!hasPurchasedSessionReplay && !hasPurchasedAdopt && !hasPurchasedAdoptReseller"
                        class="nav-item replay-link">
                        <replay-nav-item
                            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.REPLAY_UPSELL)"
                            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.REPLAY_UPSELL) && 'page'"
                            :expanded="expanded" />
                    </li>
                    <li
                        v-if="!hasPurchasedListen || isListenFreemium"
                        class="nav-item listen-link">
                        <listen-nav-item
                            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.LISTEN_UPSELL)"
                            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.LISTEN_UPSELL) && 'page'"
                            :expanded="expanded" />
                    </li>
                    <li
                        v-if="showSalesforceUpsell"
                        class="nav-item salesforce-link">
                        <salesforce-nav-item
                            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.SALESFORCE_UPSELL)"
                            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.SALESFORCE_UPSELL) && 'page'"
                            :expanded="expanded" />
                    </li>
                    <li
                        v-if="!hasPurchasedPredict"
                        class="nav-item predict-link">
                        <predict-nav-item
                            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.PREDICT_UPSELL)"
                            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.PREDICT_UPSELL) && 'page'"
                            :expanded="expanded" />
                    </li>
                </ul>
            </div>

            <div class="side-nav__bottom">
                <div class="side-nav__embedded-guide-container" />

                <upgrade-nav-item
                    v-if="showUpgradeButton"
                    :expanded="expanded"
                    :subscription-package-tiers="subscriptionPackageTiers"
                    :show-starter-upgrade-page="showStarterUpgradePage"
                    :is-free-and-not-india="isFreeAndNotIndia" />
            </div>
        </nav>
        <contact-sales-form />
        <free-trial-form
            :subscription-setup-key="activeSubscription.setupKey"
            :is-installed="isSnippetInstalled" />
        <welcome-mat />
        <voc-feedback-form-drawer
            v-if="canSubmitFeedback"
            :visible="showFeedbackForm"
            :query="feedbackFormQuery"
            @closed="onCloseFeedbackForm" />
        <import-feedback-drawer
            v-if="feedbackEnabled"
            :visible="showImportDrawer"
            :automatic-import-config="automaticImportConfig"
            :page-origin="importDrawerPageOrigin"
            @closed="onCloseImportDrawer"
            @view-created="emitImportDrawerAction('importFeedbackDrawerViewCreated')"
            @ai-settings-updated="emitImportDrawerAction('importFeedbackDrawerSetAISettings', $event)"
            @update-source-config-list="emitImportDrawerAction('updateSourceConfigList', $event)" />
        <impact-brief-wrapper
            v-if="showImpactBriefWizard"
            :show-impact-brief-wizard="showImpactBriefWizard"
            @hide-wizard="showImpactBriefWizard = false" />
    </div>
</template>

<script>
import useDarkMode from '@/common/use/use-dark-mode/use-dark-mode';
import { useFixedStyles } from '@/common/use/use-fixed-styles/use-fixed-styles';
import useUiVersion from '@/common/use/use-ui-version/use-ui-version';
import {
    EXPANDED_NAV_LS_KEY,
    SECONDARY_LEVEL_NAV_OPTIONS,
    sideNavCollapsedWidthSmallScreen,
    sideNavExpandedWidth,
    TOP_LEVEL_NAV_OPTIONS,
} from '@/constants/nav';
import { createNewV2Dashboard, DASHBOARD_CREATE_LOCATION } from '@/dashboards-v2/helpers/common.util';
import { fetchHasOnboardingAccess } from '@/onboarding-homepage/helpers/onboarding-access/onboarding-access.helper';
import useOnboarding from '@/onboarding-homepage/use/use-onboarding';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import { isOptionActive } from '@/side-nav/helpers/side-nav.helper';
import { getFirstFocusableNode } from '@/utils/dom.utils.js';
import { FREEMIUM_STATUSES, getFreemiumStatus } from '@/voc/helpers/permissions.helper';
import { asyncQueue } from '@/vuex/modules/roadmaps/async-queue/async-queue';
import { EventBus } from '@utils/event-bus.js';
import { onClickOutside } from '@vueuse/core';
import isEmpty from 'lodash/isEmpty';
import { computed, useTemplateRef, watch, defineAsyncComponent } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia';
import { useLeftNavStore } from '@/stores/left-nav.store';
import ExpandCollapseNavButton from '@/common/expand-collapse-nav-button/expand-collapse-nav-button.vue';
import GlobalCreate from '@/side-nav/components/global-create/global-create.vue';
import JumpTo from '@/side-nav/components/jump-to/jump-to.vue';
import LogoOem from '@/side-nav/components/logo/logo-oem.vue';
import Logo from '@/side-nav/components/logo/logo.vue';
import NavSectionCollapsibleItem
    from '@/side-nav/components/nav-section-collapsible-item/nav-section-collapsible-item.vue';
import NavSectionPopover from '@/side-nav/components/nav-section-popover/nav-section-popover.vue';
import QuickAccess from '@/side-nav/components/quick-access/quick-access.vue';
import AnalyticsNavItem from '@/side-nav/components/top-level-nav-items/analytics-nav-item.vue';
import DashboardsNavItem from '@/side-nav/components/top-level-nav-items/dashboards-nav-item.vue';
import GuidesNavItem from '@/side-nav/components/top-level-nav-items/guides-nav-item.vue';
import HomeNavItem from '@/side-nav/components/top-level-nav-items/home-nav-item.vue';
import ListenNavItem from '@/side-nav/components/top-level-nav-items/listen-nav-item.vue';
import OrchestrateNavItem from '@/side-nav/components/top-level-nav-items/orchestrate-nav-item.vue';
import PredictNavItem from '@/side-nav/components/top-level-nav-items/predict-nav-item.vue';
import ReplayNavItem from '@/side-nav/components/top-level-nav-items/replay-nav-item.vue';
import SalesforceNavItem from '@/side-nav/components/top-level-nav-items/salesforce-nav-item.vue';
import SpacesNavItem from '@/side-nav/components/top-level-nav-items/spaces-nav-item.vue';
import SurveysNavItem from '@/side-nav/components/top-level-nav-items/surveys-nav-item.vue';
import UpgradeNavItem from '@/side-nav/components/top-level-nav-items/upgrade-nav-item.vue';
import { PendoCollapse, PendoTooltip } from '@pendo/components-vue3';
import FreeTrialForm from '@/free/free-trial-form/free-trial-form.vue';
import ContactSalesForm from '@/free/contact-sales-form/contact-sales-form.vue';

// router permissions
import * as RoutePermissions from '@/vue-router/route-permissions.utils';
import { hasAgentModeAccess } from '@/vue-router/route-guards.utils';
import { EXPLORE_STATUSES, getExploreStatus } from '@/voc/helpers/permissions.helper';

export default {
    name: 'SideNav',
    components: {
        PendoCollapse,
        Logo,
        LogoOem,
        HomeNavItem,
        DashboardsNavItem,
        OrchestrateNavItem,
        AnalyticsNavItem,
        GuidesNavItem,
        ReplayNavItem,
        PredictNavItem,
        SurveysNavItem,
        UpgradeNavItem,
        SpacesNavItem,
        ContactSalesForm,
        FreeTrialForm,
        WelcomeMat: defineAsyncComponent(() => import('@/free/welcome-mat/welcome-mat.vue')),
        VocFeedbackFormDrawer: defineAsyncComponent(() => import('@/voc/components/drawers/voc-feedback-form-drawer/voc-feedback-form-drawer.vue')),
        ImportFeedbackDrawer: defineAsyncComponent(() => import('@/voc/components/drawers/import-feedback-drawer/import-feedback-drawer.vue')),
        ImpactBriefWrapper: defineAsyncComponent(() => import('@/impact-brief/impact-brief-wrapper.vue')),
        QuickAccess,
        GlobalCreate,
        JumpTo,
        ListenNavItem,
        SalesforceNavItem,
        ExpandCollapseNavButton,
        NavSectionCollapsibleItem,
        NavSectionPopover,
    },
    directives: {
        PendoTooltip,
    },
    setup () {
        const { hasOnboardingAccess, setHasOnboardingAccess } = useOnboarding();
        const { isPendoDarkMode } = useDarkMode();
        const { isNewUi } = useUiVersion();
        const { leftNavWidth } = useFixedStyles();
        const sideNav = useTemplateRef('sideNav');

        const leftNavStore = useLeftNavStore();
        const expanded = computed(() => leftNavStore.expanded);

        const isOnSmallScreen = computed(() => leftNavWidth.value === sideNavCollapsedWidthSmallScreen);

        const closeSideNavOnSmallScreen = () => {
            if (expanded.value && isOnSmallScreen.value) {
                leftNavStore.expandCollapseNav({ expanded: false });
            }
        };

        onClickOutside(
            sideNav,
            closeSideNavOnSmallScreen,
            {
                ignore: ['#expand-collapse-nav-button'],
            },
        );

        watch(isOnSmallScreen, (isSmall) => {
            if (isSmall) {
                closeSideNavOnSmallScreen();
            }
        });

        return {
            hasOnboardingAccess,
            isPendoDarkMode,
            isNewUi,
            leftNavWidth,
            sideNav,
            isOnSmallScreen,

            setHasOnboardingAccess,
            closeSideNavOnSmallScreen,
        };
    },
    data () {
        return {
            showFeedbackForm: false,
            showImportDrawer: false,
            automaticImportConfig: null,
            importDrawerPageOrigin: null,
            feedbackFormQuery: {},
            openPopover: '',
            mouseenterTimeout: null,
            mouseleaveTimeout: null,
            showImpactBriefWizard: false,
            expandedNavOption: null,
            TOP_LEVEL_NAV_OPTIONS,
            SIDE_NAV_EXPANDED_WIDTH: `${sideNavExpandedWidth}px`,
        };
    },
    computed: {
        ...mapState({
            dashboardsFetched: (state) => state.dashboards.fetched,
        }),
        ...mapPiniaState(useLeftNavStore, ['isBlurred', 'expanded']),
        ...mapGetters({
            hasSegmentFlag: 'auth/hasSegmentFlag',
            isFreeOrStarter: 'subscription/isFreeOrStarter',
            dashboardList: 'dashboards/ownerDashboards',
            hasProductLinePurchased: 'productLines/hasProductLinePurchased',
            isVocListenFreemiumActive: 'productLines/isVocListenFreemiumActive',
            productLineByType: 'productLines/productLineByType',
            activeSubscription: 'subscription/getActiveSub',
            getFeatureFlag: 'subscription/getFeatureFlag',
            subscriptionPackageTiers: 'subscription/subscriptionPackageTiers',
            settingByName: 'userSettings/settingByName',
            isSnippetInstalled: 'subscription/snippetInstalled',
            canSubmitFeedback: 'vocItems/canSubmitFeedback',
            isEligibleForTrial: 'subscription/isEligibleForTrial',
            isTrainingSubscription: 'subscription/isTrainingSubscription',
            hasPurchasedSessionReplay: 'subscription/hasSessionReplayEnabled',
            isFreeAndNotIndia: 'subscription/isFreeAndNotIndia',
        }),
        headerMarginStyle () {
            const marginBottom = this.hasPurchasedAdoptEndUser ? '8px' : '20px';

            return {
                '--header-margin-bottom': marginBottom,
            };
        },
        learnNavOptions () {
            return [
                {
                    name: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                    title: this.$t('sideNav.product.Product'),
                    collapsible: true,
                    iconComponent: 'ProductNavIcon',
                    pendoAnalytics: 'product-nav-item',
                    children: [
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.APPS,
                            title: this.$t('sideNav.product.Apps'),
                            route: { name: 'Apps' },
                            pendoAnalytics: 'apps',
                            hasRedirect: () => RoutePermissions.hasAppHealthRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.OBJECT_ANALYTICS,
                            title: this.$t('sideNav.product.Object analytics'),
                            route: { name: 'ObjectAnalytics' },
                            pendoAnalytics: 'object-analytics',
                            hasRedirect: () => RoutePermissions.hasObjectAnalyticsRedirect(),
                            tag: {
                                type: 'beta',
                                size: 'small',
                            },
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.PAGES,
                            title: this.$t('sideNav.product.Pages'),
                            route: { name: 'Pages' },
                            pendoAnalytics: 'pages',
                            hasRedirect: () => RoutePermissions.hasOEMPagesAndFeaturesRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.FEATURES,
                            title: this.$t('sideNav.product.Features'),
                            route: { name: 'Features' },
                            pendoAnalytics: 'features',
                            hasRedirect: () => RoutePermissions.hasOEMPagesAndFeaturesRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.TRACK_EVENTS,
                            title: this.$t('sideNav.product.Track Events'),
                            route: { name: 'TrackEvents' },
                            pendoAnalytics: 'track-events',
                            hasRedirect: () => RoutePermissions.hasOEMProductAnalyticsRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.PRODUCT_AREAS,
                            title: this.$t('sideNav.product.Product Areas'),
                            route: { name: 'ProductAreas' },
                            pendoAnalytics: 'product-areas',
                            hasRedirect: () => RoutePermissions.hasProductAreasRedirect(),
                            tag: {
                                type: 'UpsellTag',
                            },
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.AI_AGENTS,
                            title: this.$t('sideNav.product.Agent Analytics'),
                            route: { name: 'AiAgents' },
                            pendoAnalytics: 'agent-analytics',
                            hasRedirect: () => RoutePermissions.hasAiAgentsRedirect(),
                            tag: {
                                type: 'new',
                                size: 'small',
                            },
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.PORTFOLIO,
                            title: this.$t('sideNav.product.Portfolio'),
                            route: { name: 'Portfolio' },
                            pendoAnalytics: 'portfolio',
                            hasRedirect: () =>
                                RoutePermissions.hasOEMProductAnalyticsRedirect() ||
                                (RoutePermissions.hasPortfolioOverviewRedirect() &&
                                    RoutePermissions.hasApplicationUsageRedirect() &&
                                    RoutePermissions.hasLicenseUtilizationRedirect()),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.PORTFOLIO_INSIGHTS,
                            title: this.$t('sideNav.product.Portfolio insights'),
                            route: { name: 'PortfolioInsights' },
                            pendoAnalytics: 'portfolio-insights',
                            hasRedirect: () => RoutePermissions.hasPortfolioInsightsRedirect(),
                            tag: {
                                type: 'new',
                                size: 'small',
                            },
                        },
                    ],
                },
                {
                    name: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                    title: this.$t('sideNav.people.People'),
                    collapsible: true,
                    iconComponent: 'PeopleNavIcon',
                    pendoAnalytics: 'people-nav-item',
                    children: [
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.VISITORS,
                            title: this.$t('sideNav.people.Visitors'),
                            route: { name: 'Visitors' },
                            pendoAnalytics: 'visitors',
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.ACCOUNTS,
                            title: this.$t('sideNav.people.Accounts'),
                            route: { name: 'Accounts' },
                            pendoAnalytics: 'accounts',
                            hasRedirect: () => RoutePermissions.hasAccountsRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.SEGMENTS,
                            title: this.$t('sideNav.people.Segments'),
                            route: { name: 'Segments' },
                            pendoAnalytics: 'segments',
                        },
                    ],
                },
                {
                    name: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                    title: this.$t('sideNav.analytics.Analytics'),
                    collapsible: true,
                    iconComponent: 'AnalyticsNavIcon',
                    pendoAnalytics: 'analytics-nav-item',
                    children: [
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
                            title: this.$t('sideNav.analytics.Reports'),
                            route: { name: 'SavedReports' },
                            pendoAnalytics: 'saved-reports',
                            hasRedirect: () => RoutePermissions.hasBasicAnalyticsRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.INSIGHTS,
                            title: this.$t('sideNav.analytics.Insights'),
                            route: { name: 'Insights' },
                            pendoAnalytics: 'insights',
                            hasRedirect: () => RoutePermissions.hasInsightsRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.WEB_ANALYTICS,
                            title: this.$t('sideNav.analytics.Web analytics'),
                            route: { name: 'WebAnalytics' },
                            pendoAnalytics: 'web-analytics',
                            hasRedirect: () => RoutePermissions.hasWebAnalyticsRedirect(),
                            tag: {
                                type: 'UpsellTag',
                            },
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.PES,
                            title: this.$t('sideNav.analytics.PES'),
                            route: { name: 'ProductEngagementScore' },
                            pendoAnalytics: 'p-e-s',
                            hasRedirect: () => RoutePermissions.hasPESRedirect(),
                            tag: {
                                type: 'UpsellTag',
                            },
                        },
                    ],
                },
                {
                    name: TOP_LEVEL_NAV_OPTIONS.REPLAY,
                    title: this.$t('sideNav.replay.Replay'),
                    collapsible: true,
                    iconComponent: 'ReplayNavIcon',
                    pendoAnalytics: 'replay-nav-item',
                    children: [
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.REPLAY_LIBRARY,
                            title: this.$t('sideNav.replay.Library'),
                            route: { name: 'SessionReplay' },
                            pendoAnalytics: 'replay-library',
                            hasRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.SAVED_CLIPS,
                            title: this.$t('sideNav.replay.Saved clips'),
                            route: { name: 'SavedClips' },
                            pendoAnalytics: 'replay-saved-clips',
                            hasRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
                        },
                    ],
                },
                {
                    name: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                    title: this.$t('sideNav.listen.Listen'),
                    collapsible: true,
                    iconComponent: 'ListenNavIcon',
                    pendoAnalytics: 'listen-nav-item',
                    ...((getFreemiumStatus() === FREEMIUM_STATUSES.OFF) ? {
                        children: [
                            {
                                name: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_GETTING_STARTED,
                                title: this.$t('sideNav.listen.Getting started'),
                                route: { name: 'ListenGetStarted' },
                                customSuffixIcon: {
                                    'type': 'arrow-right',
                                    'size': '14px',
                                    'stroke': 'var(--text-body-subtle-base)',
                                    'aria-hidden': true,
                                },
                                pendoAnalytics: 'discover-get-started',
                                hasRedirect: () => RoutePermissions.hasListenFeedbackRedirect(),
                            },
                            ...(this.hasSegmentFlag('aiNativeSignals') ? [{
                                name: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_SIGNALS,
                                title: this.$t('sideNav.listen.Signals'),
                                route: { name: 'ListenSignals' },
                                pendoAnalytics: 'listen-signals',
                                hasRedirect: () => RoutePermissions.hasListenSignalsRedirect(),
                            }] : []),
                            {
                                name: SECONDARY_LEVEL_NAV_OPTIONS.EXPLORE_FEEDBACK,
                                title: this.$t('sideNav.listen.Explore feedback'),
                                route: { name: 'ListenFeedbackExplore' },
                                pendoAnalytics: 'feedback-explore',
                                hasRedirect: () => RoutePermissions.hasListenExploreRedirect(),
                                disabled: (getExploreStatus() === EXPLORE_STATUSES.NOT_ALLOWED),
                                tooltipMessage: (getExploreStatus() === EXPLORE_STATUSES.NOT_ALLOWED) && this.$t('sideNav.listen.Explore is disabled because this subscription has data processing restrictions applied that affect its use.'),
                            },
                            {
                                name: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_FEEDBACK,
                                title: this.$t('sideNav.listen.Feedback views'),
                                route: { name: 'ValidateFeedbackSavedViews' },
                                pendoAnalytics: 'feedback',
                                hasRedirect: () => RoutePermissions.hasListenFeedbackRedirect(),
                            },
                            {
                                name: SECONDARY_LEVEL_NAV_OPTIONS.VALIDATE_IDEAS,
                                title: this.$t('sideNav.listen.Validate ideas'),
                                route: { name: 'Validate' },
                                pendoAnalytics: 'validate',
                                hasRedirect: () => RoutePermissions.hasValidateRedirect(),
                            },
                            {
                                name: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_PORTAL,
                                title: this.$t('sideNav.listen.Portals'),
                                route: { name: 'VocPortals' },
                                pendoAnalytics: 'voc-portals',
                                hasRedirect: () => RoutePermissions.hasValidateRedirect(),
                            },
                            {
                                name: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_SETTINGS,
                                title: this.$t('sideNav.listen.Listen_settings'),
                                route: { name: 'ListenSettings' },
                                pendoAnalytics: 'listen-setup',
                                hasRedirect: () => RoutePermissions.hasListenSettingsRedirect(),
                            },
                        ],
                    } : {
                        route: { name: 'ListenFreemium' },
                        hasRedirect: () => RoutePermissions.hasListenFreemiumRedirect(),
                        tag: {
                            type: 'free',
                            size: 'small',
                            label: this.$t('sideNav.listen.Preview'),
                        },
                        disabled: (getFreemiumStatus() === FREEMIUM_STATUSES.NOT_ALLOWED),
                        tooltipMessage: (getFreemiumStatus() === FREEMIUM_STATUSES.NOT_ALLOWED) && this.$t('sideNav.listen.Freemium is disabled because this subscription has data processing restrictions applied that affect its use.'),
                    }),
                },
                {
                    name: TOP_LEVEL_NAV_OPTIONS.SURVEYS,
                    title: this.$t('sideNav.Surveys'),
                    iconComponent: 'SurveysNavIcon',
                    suffixIcon: false,
                    route: { name: 'SurveysList' },
                    pendoAnalytics: 'surveys-nav-item',
                    hasRedirect: () => RoutePermissions.hasSurveysRedirect(),
                },
                {
                    name: TOP_LEVEL_NAV_OPTIONS.NPS,
                    iconComponent: 'NpsNavIcon',
                    title: this.$t('sideNav.NPS'),
                    suffixIcon: false,
                    route: { name: 'NPS' },
                    pendoAnalytics: 'nps-nav-item',
                    hasRedirect: () => RoutePermissions.hasNPSRedirect(),
                },
            ];
        },
        actNavOptions () {
            return [
                {
                    name: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                    title: this.$t('sideNav.guides.Guides'),
                    collapsible: true,
                    iconComponent: 'GuidesNavIcon',
                    pendoAnalytics: 'guides-nav-item',
                    children: [
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.GUIDES,
                            title: this.$t('sideNav.guides.Guides'),
                            route: { name: 'Guides' },
                            pendoAnalytics: 'guides',
                            hasRedirect: () => RoutePermissions.hasBasicGuidesRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.EXPERIMENTS,
                            title: this.$t('sideNav.guides.Experiments'),
                            route: { name: 'ExperimentsList' },
                            pendoAnalytics: 'guide-experiments',
                            hasRedirect: () => RoutePermissions.hasAbTestingRedirect(),
                            tag: {
                                type: 'new',
                                size: 'small',
                            },
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.FORM_VALIDATION,
                            title: this.$t('sideNav.guides.Form validation'),
                            route: { name: 'Forms' },
                            pendoAnalytics: 'form-validation',
                            hasRedirect: () => RoutePermissions.hasFormValidationRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.RESOURCE_CENTER,
                            title: this.$t('sideNav.guides.Resource Center'),
                            route: { name: 'ResourceCenter' },
                            pendoAnalytics: 'resource-center',
                            hasRedirect: () => RoutePermissions.hasResourceCenterRedirect(),
                            tag: {
                                type: 'UpsellTag',
                            },
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.AGENT_TOOLKIT,
                            title: this.$t('sideNav.guides.Agent Toolkit'),
                            route: { name: 'AgentToolkit' },
                            pendoAnalytics: 'agent-toolkit',
                            hasRedirect: () => RoutePermissions.hasAgentToolkitRedirect(),
                            tag: {
                                type: 'beta',
                                size: 'small',
                            },
                        },
                    ],
                },
                {
                    name: TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE,
                    title: this.$t('sideNav.orchestrate.Orchestrate'),
                    collapsible: true,
                    iconComponent: 'OrchestrateNavIcon',
                    pendoAnalytics: 'orchestrate-nav-item',
                    children: [
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.JOURNEYS,
                            title: this.$t('sideNav.orchestrate.Journeys'),
                            route: { name: 'Journeys' },
                            pendoAnalytics: 'journeys',
                            hasRedirect: () => RoutePermissions.hasJourneysRedirect(),
                        },
                        {
                            name: SECONDARY_LEVEL_NAV_OPTIONS.EMAILS,
                            title: this.$t('sideNav.orchestrate.Emails'),
                            route: { name: 'Emails' },
                            pendoAnalytics: 'emails',
                            hasRedirect: () => RoutePermissions.hasStandaloneEmailsRedirect(),
                        },
                    ],
                },
                {
                    name: TOP_LEVEL_NAV_OPTIONS.ROADMAPS,
                    title: this.$t('sideNav.Roadmaps'),
                    iconComponent: 'RoadmapNavIcon',
                    suffixIcon: false,
                    route: { name: 'RoadmapsList' },
                    pendoAnalytics: 'roadmaps',
                    hasRedirect: () => RoutePermissions.hasRoadmapsRedirect(),
                },
                {
                    name: TOP_LEVEL_NAV_OPTIONS.FEATURE_FLAGS,
                    title: this.$t('sideNav.Feature_flags'),
                    iconComponent: 'FeatureFlagsNavIcon',
                    suffixIcon: false,
                    route: { name: 'FeatureFlags' },
                    pendoAnalytics: 'feature-flags',
                    hasRedirect: () => RoutePermissions.hasFeatureFlagsRedirect(),
                },
            ];
        },
        hasPurchasedAnalytics () {
            return this.hasProductLinePurchased('analytics');
        },
        hasPurchasedNPS () {
            return this.hasProductLinePurchased('nps');
        },
        hasPurchasedSurveys () {
            return this.hasProductLinePurchased('surveys');
        },
        hasPurchasedAdopt () {
            return this.hasProductLinePurchased('adopt');
        },
        hasPurchasedListen () {
            return this.hasProductLinePurchased('voc', 'feedback') || this.hasProductLinePurchased('voc', 'ideas');
        },
        isListenFreemium () {
            return this.isVocListenFreemiumActive;
        },
        hasPurchasedAdoptReseller () {
            return this.hasProductLinePurchased('adoptReseller');
        },
        hasPurchasedAdoptEndUser () {
            return this.hasProductLinePurchased('adoptEndUser');
        },
        hasPurchasedPredict () {
            return this.hasProductLinePurchased('predict');
        },
        isOnTrialExpiredRoute () {
            return this.$route?.name === 'TrialExpired';
        },
        activeNavOption () {
            return this.$route?.meta?.activeNavOption || {};
        },
        showStarterUpgradePage () {
            return this.hasSegmentFlag('starterUpgradePage');
        },
        shouldShowHomeNavItem () {
            if (hasAgentModeAccess()) {
                return true;
            }

            const hasHomeDashboard = !!this.settingByName('homeDashboardId', true)?.value;

            if (
                !this.$productLine(PRODUCT_LINES.CORE, 'homepageEnabled') ||
                (hasHomeDashboard && !this.hasSegmentFlag('homeDashboardAndHomepage')) || this.isAdoptEndUser
            ) {
                return false;
            }

            const showHomepage =
                this.hasSegmentFlag('showActivationHomepage') ||
                this.subscriptionPackageTiers.isInTrial ||
                this.isFreeOrStarter;

            return this.hasOnboardingAccess || showHomepage;
        },
        shouldShowSpacesNavItem () {
            return !RoutePermissions.hasSpacesRedirect();
        },
        feedbackEnabled () {
            return this.hasProductLinePurchased('voc', 'feedback');
        },
        hasPurchasedGuides () {
            return this.hasProductLinePurchased('guides');
        },
        hasPurchasedGuidesPro () {
            if (!this.hasPurchasedGuides) return false;

            const guidesMetadata = this.productLineByType('guides').metadata;

            return (
                guidesMetadata.customCodeBlocksEnabled &&
                guidesMetadata.aIWritingAssistantEnabled &&
                guidesMetadata.aIGuideContentEnabled &&
                guidesMetadata.localizationEnabled &&
                guidesMetadata.enabledRestrictedResourceCenterModules.announcements &&
                guidesMetadata.enabledRestrictedResourceCenterModules.externalWebsite &&
                guidesMetadata.enabledRestrictedResourceCenterModules.codeBlock
            );
        },
        hasPurchasedOrchestrate () {
            return this.hasProductLinePurchased('orchestrate');
        },
        showOrchestrateSplashPage () {
            return !this.hasPurchasedOrchestrate;
        },
        showUpgradeButton () {
            const { isPendoFree, isInTrial, isStarterPackage, isStarter2024Package } = this.subscriptionPackageTiers;
            const isUpgradable = isPendoFree || isInTrial || isStarterPackage || isStarter2024Package;

            return isUpgradable;
        },
        showSalesforceUpsell () {
            return this.hasSegmentFlag('salesforceUpsell');
        },
        hasPurchasedAllAvailableModules () {
            if (this.hasPurchasedAdopt || this.hasPurchasedAdoptReseller) {
                return this.hasPurchasedAnalytics && this.hasPurchasedGuides && this.hasPurchasedGuidesPro;
            }

            return (
                this.hasPurchasedAnalytics &&
                this.hasPurchasedGuides &&
                this.hasPurchasedGuidesPro &&
                this.hasPurchasedSessionReplay &&
                this.hasPurchasedSurveys &&
                this.hasPurchasedOrchestrate &&
                this.hasPurchasedListen &&
                this.hasPurchasedPredict
            );
        },
        showAvailableUpgradeModules () {
            return (
                !this.getFeatureFlag('hideUpsellNav') &&
                !this.hasProductLinePurchased('adoptEndUser') &&
                ((!this.isTrainingSubscription && !this.hasPurchasedAllAvailableModules)
                    || this.showSalesforceUpsell)
            );
        },
        isAdoptEndUser () {
            return this.hasProductLinePurchased('adoptEndUser');
        },
    },
    watch: {
        '$route.name': function () {
            if (this.expanded && this.expandedNavOption?.[0]) {
                const activeTopOptionName =
                    typeof this.activeNavOption.topOptionName === 'function'
                        ? this.activeNavOption.topOptionName()
                        : this.activeNavOption.topOptionName;

                if (!activeTopOptionName || this.expandedNavOption[0] !== activeTopOptionName) {
                    this.expandedNavOption = null;
                }
            }

            this.closeSideNavOnSmallScreen();
        },
    },
    async created () {
        const lsExpandedState = window.localStorage.getItem(EXPANDED_NAV_LS_KEY);
        const sideNavExpanded = !lsExpandedState || lsExpandedState === 'true';

        this.expandCollapseNav({ expanded: sideNavExpanded });

        EventBus.$on('discoverFeedbackShowForm', this.onShowFeedbackForm);
        EventBus.$on('linkFeedbackToIdea', this.addLinkToAsyncQueue);
        EventBus.$on('importFeedbackDrawerOpen', this.onOpenImportDrawer);
        EventBus.$on('showImpactBrief', this.onShowImpactBrief);
        EventBus.$on('sideNavExpandedStateChanged', this.handleSideNavStateChange);

        const needsOnboardingAccess = this.hasOnboardingAccess === null;
        const promises = [this.fetchDashboards()];

        if (needsOnboardingAccess) {
            promises.unshift(fetchHasOnboardingAccess());
        }

        if (this.hasPurchasedSessionReplay) {
            promises.push(this.fetchLatestReplaySavedFilter());
        }

        const [hasOnboardingAccess] = await Promise.all(promises);

        if (needsOnboardingAccess) {
            this.setHasOnboardingAccess(hasOnboardingAccess);
        }

        this.updateCurrentMauLimit();
    },
    mounted () {
        this.openTrialFormIfParamExists();
        this.openFeedbackFormIfParamExists();
        this.openImpactBriefWizardIfParamExists();

        setTimeout(() => {
            this.showTrialGuide();
        }, 750);
    },
    beforeUnmount () {
        EventBus.$off('discoverFeedbackShowForm', this.onShowFeedbackForm);
        EventBus.$off('linkFeedbackToIdea', this.addLinkToAsyncQueue);
        EventBus.$off('importFeedbackDrawerOpen', this.onOpenImportDrawer);
        EventBus.$off('showImpactBrief', this.onShowImpactBrief);

        this.clearMouseenterTimeout();
        this.clearMouseleaveTimeout();
    },
    methods: {
        ...mapActions({
            loadDashboards: 'dashboards/loadAll',
            updateCurrentMauLimit: 'subscription/updateCurrentMauLimit',
            updateUserSetting: 'userSettings/update',
            loadSavedFilters: 'savedFilters/loadAll',
        }),
        ...mapPiniaActions(useLeftNavStore, ['expandCollapseNav']),
        filterNavOptionsByPermissions (navOptions) {
            return navOptions.reduce((acc, navOption) => {
                if (navOption.children) {
                    const children = navOption.children.filter((child) => {
                        if (child.tag?.type === 'UpsellTag') {
                            const redirect = child.hasRedirect?.();

                            if (redirect?.name === 'UpgradeRequired') {
                                return true;
                            }
                        }

                        return !child.hasRedirect?.() || child.disabled;
                    });

                    if (!isEmpty(children)) {
                        acc.push({ ...navOption, children });
                    }
                } else if (!navOption.hasRedirect?.()) {
                    acc.push(navOption);
                }

                return acc;
            }, []);
        },
        navItemActive (navItemName) {
            return isOptionActive(navItemName, this.activeNavOption.topOptionName);
        },
        openTrialForm () {
            EventBus.$emit('openFreeTrialForm');
        },
        openTrialFormIfParamExists () {
            if (!this.isEligibleForTrial) return;
            const params = new URLSearchParams(window.location.search);
            if (params.get('trialForm') === 'true') {
                this.openTrialForm();
            }
        },
        showUpfrontTrialGuide () {
            const guide = pendo.guides?.find((guide) =>
                guide.name.includes('PLG | Upfront Trial | Welcome to your trial'),
            );

            if (!guide) return;

            const isUpfrontTrialSub = this.getFeatureFlag('hadUpfrontTrial');
            const hasSeenGuide = guide?.hasBeenSeen();

            if (isUpfrontTrialSub && !hasSeenGuide && guide) {
                guide.show();
            }
        },
        showEndOfTrialGuide () {
            const guide = pendo.guides?.find((guide) => guide.name.includes('PLG | Free Trial expired'));

            const seenTrialSetting = this.settingByName('seenTrial', true)?.value;

            if (seenTrialSetting && guide) {
                guide.show();
                this.updateUserSetting({ namespaced: true, name: 'seenTrial', value: false });
            }
        },
        showTrialGuide () {
            const { isPendoFree, isInTrial } = this.subscriptionPackageTiers;

            if (isInTrial) {
                if (!this.settingByName('seenTrial', true)?.value) {
                    this.updateUserSetting({ namespaced: true, name: 'seenTrial', value: true });
                }

                this.showUpfrontTrialGuide();
            } else if (isPendoFree) {
                this.showEndOfTrialGuide();
            }
        },
        onShowFeedbackForm (options = {}) {
            const { query } = options;

            this.feedbackFormQuery = query || {};
            this.showFeedbackForm = true;
            this.addDrawerOpenParam();
        },
        openFeedbackFormIfParamExists () {
            const params = new URLSearchParams(window.location.search);
            if (params.get('context') === 'createFeedbackForm') {
                this.onShowFeedbackForm();
            }
        },
        openImpactBriefWizardIfParamExists () {
            const params = new URLSearchParams(window.location.search);
            if (params.get('showImpactBrief')) {
                this.showImpactBriefWizard = true;
            }
        },
        onCloseFeedbackForm (itemToOpen) {
            this.showFeedbackForm = false;
            this.removeDrawerParams();
            if (itemToOpen) {
                this.$router.push({
                    name: 'ListenFeedbackAllFeedbackView',
                    query: { openItem: itemToOpen },
                });
            }
        },
        addDrawerOpenParam () {
            const newQuery = this.$route.query;
            this.$router.replace({ query: { ...newQuery, drawerOpen: true } });
        },
        removeDrawerParams () {
            const { context, drawerOpen, globalCreate, ...newQuery } = this.$route.query;

            this.$router.replace({ query: newQuery });
        },
        onCloseImportDrawer () {
            this.showImportDrawer = false;
            EventBus.$emit('importFeedbackDrawerClosed');
        },
        emitImportDrawerAction (action, ...payload) {
            EventBus.$emit(action, ...payload);
        },
        onOpenImportDrawer (automaticImportConfig = null, pageOrigin = 'listen') {
            this.automaticImportConfig = automaticImportConfig;
            this.importDrawerPageOrigin = pageOrigin;
            this.showImportDrawer = true;
        },
        async fetchLatestReplaySavedFilter () {
            try {
                await this.loadSavedFilters({ type: 'replaylibrary' });
            } catch (error) {

                console.error('error loading replay library saved filters', error);
            }
        },
        async fetchDashboards () {
            if (!this.dashboardsFetched) {
                await this.loadDashboards();
            }
            if (this.dashboardList.length === 0) {
                createNewV2Dashboard(DASHBOARD_CREATE_LOCATION.LEFT_NAV);
            }
        },
        addLinkToAsyncQueue ({ task }) {
            return asyncQueue.enqueue({
                task,
                updateType: 'noUpdate',
            });
        },
        onShowImpactBrief () {
            this.showImpactBriefWizard = true;
        },
        handlePopover (optionName) {
            if (optionName) {
                this.openPopover = optionName;
            } else {
                this.openPopover = '';
            }
        },
        handleMouseenter (optionName) {
            this.clearMouseenterTimeout();
            this.clearMouseleaveTimeout();

            if (this.openPopover !== optionName) {
                this.mouseenterTimeout = setTimeout(() => {
                    this.handlePopover(optionName);
                }, 250);
            }
        },
        handleMouseleave () {
            this.clearMouseenterTimeout();
            this.clearMouseleaveTimeout();

            this.mouseleaveTimeout = setTimeout(() => {
                this.handlePopover();
            }, 250);
        },
        clearMouseenterTimeout () {
            if (this.mouseenterTimeout) {
                clearTimeout(this.mouseenterTimeout);
                this.mouseenterTimeout = null;
            }
        },
        clearMouseleaveTimeout () {
            if (this.mouseleaveTimeout) {
                clearTimeout(this.mouseleaveTimeout);
                this.mouseleaveTimeout = null;
            }
        },
        handleSideNavStateChange () {
            if (!this.expanded) {
                this.expandedNavOption = null;
            }

            if (this.expanded && this.isOnSmallScreen) {
                this.$nextTick(() => {
                    getFirstFocusableNode(this.$refs.sideNav)?.focus();
                });
            }
        },
        hasChildren (option) {
            return option.children?.length > 0;
        },
    },
};
</script>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.tooltip.pendo-tooltip.left-nav-tooltip {
    /* Ensure tooltip appears underneath guides targeting the nav bar */
    z-index: 499 !important;
}
</style>

<style scoped lang="less">
@import '@/side-nav/nav.style.less';

.side-nav {
    .screen-size(@zero, @large, {
        position: fixed;
        top: var(--top-nav-height);
        left: 0;
        bottom: 0;
        z-index: 501;
        height: calc(100vh - var(--top-nav-height));

        .side-nav__header {
            display: none;
        }

        .side-nav__content {
            &:not(.expanded) {
                display: none;
            }

            &.expanded {
                padding: 16px 2px;
            }
        }
    });

    :deep(.pendo-menu-panel) {
        width: 180px;
    }

    :deep(.pendo-menu-panel .pendo-menu-panel) {
        width: 334px;
        max-height: calc(100vh - var(--top-nav-height));
        overflow-y: auto;

        .screen-size(@zero, @medium, {
            width: 200px;
            max-height: calc(100vh - 60px);
        });

        .pendo-menu-items {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .pendo-menu-item {
            padding: 0 16px;
        }

        .global-create__option-text {
            padding: 10px 0;
        }

        .global-create__option .pendo-icon {
            align-self: flex-start;
            padding-top: 9px;
        }
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        margin-bottom: var(--header-margin-bottom);
        gap: 14px;

        &.expanded {
            justify-content: flex-start;
            padding: 0 16px;
        }
    }

    &__content {
        background-color: var(--surface-secondary-background);
        height: 100%;
        z-index: 502;
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 59px;
        border-right: 1px solid var(--surface-default-border);
        padding: 10px 6px;

        .nav-title {
            padding-left: 23px;
        }

        .nav-item {
            display: flex;
            justify-content: center;
            width: 100%;
        }

        .nav-list {
            list-style-type: none;
            padding: 0;
            margin: 0;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;

            & > li {
                width: 100%;
            }
        }

        .nav-section {
            width: 100%;
            margin-top: 20px;

            &__collapse {
                border: none;
            }

            .nav-title {
                padding-left: 13px;
            }
        }

        &.sales-trial-expired {
            .side-nav__logo,
            .nav-item,
            :deep(.global-create__trigger),
            :deep(.collapsible-nav-option) {
                cursor: not-allowed;
                opacity: 0.7;
                pointer-events: none;

                * {
                    pointer-events: none;
                }
            }

            :deep(.global-create__trigger):hover {
                background: var(--static-palette-pink-130);
            }
        }

        &.expanded {
            width: v-bind('SIDE_NAV_EXPANDED_WIDTH');
            padding: 10px 2px;

            .nav-section {
                padding-left: 10px;
                padding-right: 10px;
            }

            .nav-item {
                padding: 0 10px;
            }

            .nav-item.recents-link,
            .nav-item.jump-to-menu {
                padding: 0;

                :deep(.pendo-popover-v3__reference) {
                    width: 100%;
                    padding: 0 10px;
                    display: flex;
                    justify-content: center;
                }
            }

            :deep(.upgrade-button) {
                width: 100%;
            }
        }

        &:not(.expanded) {
            :deep(.nav-option) {
                .nav-icon {
                    padding: 2px;
                    border-radius: 5px;
                }

                &:focus-visible {
                    .nav-icon {
                        outline: 2px solid var(--palette-gray-90);
                        outline-offset: -2px;
                    }
                }

                &:hover {
                    .nav-icon {
                        cursor: pointer;
                    }
                }
            }

            .available-upgrade-items__separator {
                margin: 22px 0px 22px -10px;
                width: calc(100% + 20px);
            }
        }
    }

    &--new-ui {
        .side-nav__content {
            border-right: 0;
            padding-top: calc(10px + var(--new-ui-shell-margin, 20px));
        }

        .screen-size(@zero, @large, {
            position: fixed;
            top: var(--top-nav-height);
            left: var(--new-ui-shell-margin, 20px);
            bottom: var(--new-ui-shell-margin, 20px);
            height: calc(100vh - var(--top-nav-height) - var(--new-ui-shell-margin, 20px));

            .side-nav__content.expanded {
                border: 1px solid var(--palette-gray-40);
                border-top: 0;
                border-bottom-left-radius: 12px;
            }
        });
    }

    &__bottom {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: auto;
        width: 100%;
        padding: 16px 10px 0 10px;
    }
}

.available-upgrade-items {
    width: 100%;

    ul {
        display: flex;
        flex-direction: column;
        align-items: center;
        list-style-type: none;
        padding: 0;
        margin: 0;
    }

    &__separator {
        margin: 22px 0px 22px -14px;
        width: calc(100% + 28px);
        border-top: 0.5px solid var(--surface-default-border);
    }
}
</style>
